import { environment } from '@environments/environment';
import { LogLevel, type PassedInitialConfig } from 'angular-auth-oidc-client';

/**
 * Get the redirect URL for OAuth callbacks.
 * Includes the base href (e.g., /en/ or /fr/) to ensure the callback
 * lands on the correct locale path where the SPA can handle it.
 */
function getRedirectUrl(): string {
  const baseHref = document.querySelector('base')?.getAttribute('href') || '/';
  return window.location.origin + baseHref;
}

export const authConfig: PassedInitialConfig = {
  config: {
    authority: environment.cognitoUrl,
    redirectUrl: getRedirectUrl(),
    postLogoutRedirectUri: environment.url,
    clientId: environment.cognitoClientId,
    scope: 'email openid phone profile',
    responseType: 'code',
    // Verbose logging outside of production makes it much easier to diagnose
    // token renewal issues. Prod stays at Error to avoid leaking tokens/claims
    // into the browser console.
    logLevel: environment.enableLogging ? LogLevel.Debug : LogLevel.Error,

    silentRenew: true,
    useRefreshToken: true,
    // Cognito never returns a new refresh_token in token-refresh responses —
    // it only issues one on the initial authorization code exchange. Without
    // this flag, angular-auth-oidc-client drops the stored refresh token when
    // it doesn't see one in the renewal response, causing the next renewal to
    // fail with "no refresh token found" and logging the user out. This flag
    // tells the library to retain the existing refresh token in that case.
    allowUnsafeReuseRefreshToken: true,
    ignoreNonceAfterRefresh: true,
    // Cognito issues ID + access tokens with the same TTL, so if the access
    // token is still fresh but the ID token is expired we still want to renew
    // rather than silently serve requests with an expired ID token.
    triggerRefreshWhenIdTokenExpired: true,
    // 30s was too aggressive: background-tab timer throttling can delay the
    // scheduled renewal past token expiry, producing a guaranteed 401 on the
    // first request after the tab regains focus. 90s gives comfortable headroom.
    renewTimeBeforeTokenExpiresInSeconds: 90,
  },
};
